'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

interface DemoAccount {
  label: string;
  role: string;
  email: string;
  password: string;
  color: string;
}

const DEMO_ACCOUNTS: DemoAccount[] = [
  { label: 'Receptionist', role: 'Receptionist', email: 'demo.receptionist@centralinolab.com', password: 'Demo1234!', color: 'bg-sky-600 hover:bg-sky-500' },
  { label: 'Operatore',    role: 'Operatore',    email: 'demo.operatore@centralinolab.com',    password: 'Demo1234!', color: 'bg-emerald-600 hover:bg-emerald-500' },
  { label: 'Supervisore',  role: 'Supervisore',  email: 'demo.supervisore@centralinolab.com',  password: 'Demo1234!', color: 'bg-amber-600 hover:bg-amber-500' },
  { label: 'Admin',        role: 'Admin',        email: 'demo.admin@centralinolab.com',        password: 'Demo1234!', color: 'bg-orange-600 hover:bg-orange-500' },
  { label: 'SuperAdmin',   role: 'SuperAdmin',   email: 'demo.superadmin@centralinolab.com',   password: 'Demo1234!', color: 'bg-violet-600 hover:bg-violet-500' },
];

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState<string | null>(null);

  const signIn = async (loginEmail: string, loginPassword: string) => {
    setError(null);
    setLoading(true);
    try {
      const supabase = createClient();
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword,
      });

      if (authError) {
        // Show the actual error for easier debugging
        const msg = authError.message?.toLowerCase() ?? '';
        if (msg.includes('invalid login') || msg.includes('invalid credentials') || msg.includes('email not confirmed')) {
          setError(`Credenziali non valide. (${authError.message})`);
        } else {
          setError(`Errore di accesso: ${authError.message}`);
        }
        setLoading(false);
        setDemoLoading(null);
        return;
      }

      if (data?.user) {
        localStorage.setItem('lcm_current_operator_id', data.user.id);
      }

      await new Promise((resolve) => setTimeout(resolve, 150));

      const role = data?.user?.user_metadata?.role ?? '';
      if (role === 'SuperAdmin') {
        try {
          await fetch('/api/superadmin/auth', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
          });
        } catch {
          // non-blocking
        }
        router.push('/superadmin');
      } else {
        router.push('/call-dashboard');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore sconosciuto';
      setError(`Errore durante il login: ${message}`);
      setLoading(false);
      setDemoLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Inserisci email e password.');
      return;
    }
    await signIn(email, password);
  };

  const handleDemoLogin = async (account: DemoAccount) => {
    setDemoLoading(account.role);
    setEmail(account.email);
    setPassword(account.password);
    setError(null);
    await signIn(account.email, account.password);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-violet-600 mb-4 shadow-lg shadow-violet-900/40">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Lab Call Manager</h1>
          <p className="text-gray-400 text-sm mt-1">Accedi al tuo account</p>
        </div>

        {/* Login Form */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                placeholder="email@centralinolab.com"
                className="w-full px-3.5 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                placeholder="••••••••"
                className="w-full px-3.5 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
              />
            </div>

            {error && (
              <div role="alert" className="flex items-center gap-2 text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || demoLoading !== null}
              className="w-full py-2.5 px-4 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-all duration-150 shadow-lg shadow-violet-900/30 flex items-center justify-center gap-2"
            >
              {loading && demoLoading === null ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Accesso in corso…
                </>
              ) : (
                'Accedi'
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <a href="/forgot-password" className="text-sm text-violet-400 hover:text-violet-300 transition-colors">
              Password dimenticata?
            </a>
          </div>
        </div>

        {/* Demo Accounts */}
        <div className="mt-5 bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Accesso rapido demo</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.role}
                onClick={() => handleDemoLogin(account)}
                disabled={loading || demoLoading !== null}
                className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-white text-sm font-medium transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${account.color}`}
              >
                <span className="flex items-center gap-2">
                  {demoLoading === account.role ? (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                  {account.label}
                </span>
                <span className="text-xs opacity-70 font-normal truncate max-w-[180px]">{account.email}</span>
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500 text-center">Password: <span className="font-mono text-gray-400">Demo1234!</span></p>
        </div>
      </div>
    </div>
  );
}
